import {useContext, useEffect} from 'react';
import {useTranslation} from 'react-i18next';
import {useHistory, useParams, useRouteMatch} from 'react-router-dom';
import {UserCard} from '../../../../../../backend/interfaces/user-card';
import {UserCardForm} from '../../../../../../components/user-card-form/user-card-form';
import {OfficeContext} from '../../../../../../contexts/office/context';
import {OfficeContextType} from '../../../../../../contexts/office/types';
import {SnackBarContext} from '../../../../../../contexts/snack-bar/context';
import {SnackBarContextType} from '../../../../../../contexts/snack-bar/types';
import {translations} from '../../../../../../i18n/translations';
import {UserCardsRepository} from '../../../../../../repositories/user-cards';
import {CaregiverMode, ScreenPaths} from '../../../../../../enums';

export default function UserCardSystemAdministratorOfficeScreen() {
  // Get params
  let {id}: {id?: string} = useParams();
  if (id?.toLowerCase() === 'create') {
    id = undefined;
  }

  // Get context
  const snackBarContext: SnackBarContextType = useContext(SnackBarContext);
  const officeContext: OfficeContextType = useContext(OfficeContext);

  // The `path` lets us build <Route> paths that are
  // relative to the parent route, while the `url` lets
  // us build relative links.
  let {url} = useRouteMatch();

  // Get navigation info
  let history = useHistory();

  // Translations
  const {t} = useTranslation();

  // Validate and get the card
  useEffect(() => {
    const getCard = async () => {
      if (id) {
        try {
          // Get card
          const cardDetails: UserCard = await UserCardsRepository.instance.getById(id);

          if (cardDetails) {
            // Set card for edit
            officeContext.setUserCardForm(cardDetails);
          } else {
            // Error
            throw new Error('Empty card.');
          }
        } catch (error) {
          // Log
          console.debug('🔴 Could not get card.', error);

          // Show toast error
          snackBarContext.setOfficeToastNotification({
            severity: 'error',
            message: t(translations.user_cards.edit.errors.card_does_not_exist_error),
          });

          // Redirect
          history.push(`${url.replace(`/${id}`, '')}`);
        }
      } else {
        // Set card for create
        officeContext.setUserCardForm({
          isActive: true,
          tagFingerprint: '',
        });
      }
    };

    // Get card
    getCard();

    // Clean up
    return () => {
      // Clear card
      officeContext.clearUserCardForm();

      // Log
      console.debug('🧹 Card form cleaned in office context.');
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  return (
    <UserCardForm
      caregiverMode={CaregiverMode.SystemAdministrator}
      redirectUrl={id ? `${url.replace(`/${id}`, '')}` : `${url.replace('/create', '')}`}
      canWrite
      showPageTitle
      showExitButton
      titleCrumbs={[
        {
          label: t(translations.office.professional_caregiver.tabs.user_cards),
          link: `${ScreenPaths.Office_SystemAdministrator}${ScreenPaths.SystemAdministrator_UserCards}`,
        },
      ]}
    />
  );
}
